/**
 * Engine display-name helpers for the Database Blueprint surfaces (Finding #22b).
 *
 * `source.engine.toUpperCase()` produced `(SQLITE)` / `(PGSQL)` in the setup
 * dialog. The central engine registry already carries proper display names
 * — we read them directly and fall back to the upper-cased id only when the
 * registry doesn't recognise the engine.
 */

import { ENGINE_REGISTRY } from '../../../core/engines/engineDefinitions';
import type { UserFacingEngineId } from '../types/databaseBlueprintTypes';

export function engineDisplayName(engine: UserFacingEngineId): string {
  const def = ENGINE_REGISTRY[engine];
  return def?.display?.displayName ?? engine.toUpperCase();
}

/**
 * True for engines that have a real schema concept distinct from the database
 * (MSSQL `dbo`/custom, Postgres `public`/custom, Oracle users-as-schemas).
 *
 * SQLite and MySQL use database-as-schema: the "Included schemas" section in
 * the setup dialog is meaningless for them — list-schemas returns a single
 * pseudo-entry (`main` / the database name) which the user can't usefully
 * filter against. The setup dialog hides the entire section for those.
 */
export function engineHasSchemas(engine: UserFacingEngineId): boolean {
  return engine === 'mssql' || engine === 'pgsql' || engine === 'oracle';
}

/**
 * Sanitise a default label so it reads as a valid environment name
 * (e.g. "Dev", "prod-east", "music-db"). Strips the engine prefix that the
 * old code prepended ("sqlite-Sample Music DB" → "Sample-Music-DB"), then
 * collapses whitespace to dashes and trims non-label characters.
 *
 * Returns the empty string when the input boils down to nothing — the dialog
 * placeholder "e.g. Dev, Stage, Prod" then guides the user.
 */
export function sanitizeDefaultBlueprintLabel(database: string): string {
  if (!database) return '';
  return database
    .trim()
    // Drop characters that the label sanitiser would reject anyway.
    .replace(/[^A-Za-z0-9._ -]+/g, '')
    .trim()
    // Whitespace runs → single dash so the label reads as one token.
    .replace(/\s+/g, '-');
}
